// Clone of donationReceiptGenerator.js with sample watermark
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import QRCode from 'qrcode';

// Import all the same styles to maintain consistency
const STYLES = {
  gradients: {
    header: ['#2563EB', '#059669'],
    footer: ['#1E40AF', '#047857']
  },
  fonts: {
    title: 'helvetica',
    body: 'helvetica'
  },
  colors: {
    primary: '#2563EB',
    secondary: '#059669',
    textDark: '#1F2937',
    textLight: '#6B7280',
    white: '#FFFFFF'
  }
};

// Update to use Web Crypto API
const generateSecurityHash = async (data) => {
  const str = JSON.stringify({
    ...data,
    timestamp: new Date().toISOString()
  });
  
  // Convert string to buffer
  const msgBuffer = new TextEncoder().encode(str);
  
  // Generate hash using Web Crypto API
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
  
  // Convert buffer to hex string
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  
  return hashHex.slice(0, 8);
};

// Update the generateSampleQRCode function to match original
const generateSampleQRCode = async (url) => {
  try {
    return await QRCode.toDataURL(url || window.location.href, {
      width: 100,
      margin: 1,
      color: {
        dark: '#2563eb',
        light: '#ffffff'
      }
    });
  } catch (err) {
    console.error('QR Code Error:', err);
    return null;
  }
};

// Update the header/footer function to include complete footer
const addSampleHeaderFooter = (doc, causeData, qrCodeUrl, receiptId) => {
  const { width, height } = doc.internal.pageSize;

  // Dark header background
  doc.setFillColor(17, 24, 39);
  doc.rect(0, 0, width, 100, 'F');

  // Organization name with gradient text effect
  doc.setFont(STYLES.fonts.title, 'bold');
  doc.setFontSize(28);
  
  const nameX = 40;
  const nameY = 55;
  
  // Blue shadow
  doc.setTextColor(37, 99, 235);
  doc.text(causeData.name, nameX + 0.5, nameY + 0.5);
  
  // Green overlay
  doc.setTextColor(5, 150, 105);
  doc.text(causeData.name, nameX, nameY);

  // Dark footer background
  doc.setFillColor(17, 24, 39);
  doc.rect(0, height - 60, width, 60, 'F');

  // QR Code
  if (qrCodeUrl) {
    doc.addImage(qrCodeUrl, 'PNG', width - 80, height - 80, 50, 50);
    doc.setFontSize(8);
    
    // Blue shadow for gradient effect
    doc.setTextColor(37, 99, 235);
    doc.text('Scan to Support Us Again', width - 54.8, height - 19.8, { align: 'center' });
    
    // Green overlay for gradient effect
    doc.setTextColor(5, 150, 105);
    doc.text('Scan to Support Us Again', width - 55, height - 20, { align: 'center' });
  }

  // Footer text - moved higher and styled
  doc.setFont(STYLES.fonts.body, 'normal');
  doc.setFontSize(9);
  doc.setTextColor(STYLES.colors.secondary);
  doc.text(
    `${causeData.name} is a fiscally sponsored project of Cause and Impact, Inc. (EIN: 32-0977089)`,
    width/2,
    height - 35,
    { align: 'center' }
  );

  // Receipt ID - kept at bottom
  doc.setFontSize(9);
  doc.setTextColor(STYLES.colors.white);
  doc.text(`Receipt ID: ${receiptId}`, 40, height - 15);
};

// Update addSampleDonationBox to match real version exactly
const addSampleDonationBox = (doc, donationData) => {
  const { width } = doc.internal.pageSize;
  const startY = 180;
  const boxHeight = 130;

  // Thank you message
  doc.setFont(STYLES.fonts.body, 'normal');
  doc.setTextColor(STYLES.colors.textDark);
  doc.setFontSize(12);
  doc.text(`Dear ${donationData.donor.name},`, 40, 150);
  doc.text('Thank you for your generous contribution to our mission.', 40, 165);

  // Make box taller with proper spacing
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(40, startY, width - 80, boxHeight, 10, 10, 'F');
  doc.setLineWidth(0.5);
  doc.setDrawColor(STYLES.colors.primary);
  doc.roundedRect(40, startY, width - 80, boxHeight, 10, 10, 'S');

  // Donation details
  doc.setFont(STYLES.fonts.body, 'bold');
  doc.setFontSize(14);
  doc.setTextColor(STYLES.colors.primary);
  doc.text('Donation Summary', 60, startY + 25);

  const details = [
    ['Donation Type:', donationData.type],
    ['Amount:', `$${donationData.amount.toLocaleString()}`],
    ['Date:', new Date(donationData.date).toLocaleDateString()]
  ];

  details.forEach(([label, value], i) => {
    const yPos = startY + 50 + (i * 20);
    doc.setFont(STYLES.fonts.body, 'normal');
    doc.setFontSize(11);
    doc.setTextColor(STYLES.colors.textLight);
    doc.text(label, 60, yPos);

    // Special styling for amount
    if (label === 'Amount:') {
      doc.setTextColor(STYLES.colors.secondary);
      doc.setFont(STYLES.fonts.body, 'bold');
    } else {
      doc.setTextColor(STYLES.colors.textDark);
      doc.setFont(STYLES.fonts.body, 'normal');
    }
    doc.text(value, width - 60, yPos, { align: 'right' });
  });

  // Add "No goods or services" text
  const noGoodsY = startY + 115;
  doc.setFont(STYLES.fonts.body, 'italic');
  doc.setFontSize(9);
  doc.setTextColor(STYLES.colors.textLight);
  doc.text('No goods or services were provided in exchange for this contribution.', 60, noGoodsY);

  return startY + boxHeight + 10;
};

// Update addSampleIRSDisclosure to match real version
const addSampleIRSDisclosure = (doc, causeData, startY) => {
  const { width } = doc.internal.pageSize;

  doc.setFillColor(249, 250, 251);
  doc.roundedRect(40, startY, width - 80, 120, 10, 10, 'F');

  doc.setFont(STYLES.fonts.body, 'bold');
  doc.setTextColor(STYLES.colors.primary);
  doc.setFontSize(12);
  doc.text('Required IRS Disclosure', 60, startY +20);

  doc.setFont(STYLES.fonts.body, 'normal');
  doc.setFontSize(10);
  doc.setTextColor(STYLES.colors.textDark);

  const disclosureText = [
    `This letter serves as your official receipt from ${causeData.name}, a fiscally sponsored`,
    'project of Cause and Impact, Inc., a tax-exempt organization under Section 501(c)(3)',
    'of the Internal Revenue Code.',
    '',
    'Federal Tax ID (EIN): 32-0977089',
    '',
    'Please retain this receipt for your tax records.'
  ];

  let yPos = startY + 35;
  disclosureText.forEach((line, index) => {
    if (index === 4) {
      doc.setFont(STYLES.fonts.body, 'bold');
      doc.setTextColor(STYLES.colors.primary);
    } else {
      doc.setFont(STYLES.fonts.body, 'normal');
      doc.setTextColor(STYLES.colors.textDark);
    }
    if (line) doc.text(line, 60, yPos);
    yPos += line ? 15 : 8;
  });
};

// Update the addMicroTextBorder function for visible side borders
const addMicroTextBorder = (doc) => {
  const { width, height } = doc.internal.pageSize;
  const microText = 'SAMPLE RECEIPT - NOT VALID FOR TAX PURPOSES - ';
  doc.setFontSize(4);
  doc.setTextColor(220, 20, 60); // Crimson red
  
  const borderSpacing = 25; // Spacing between repetitions of the text
  const sideMargin = 10; // Margin from edges for side borders
  const topMargin = 15; // Top and bottom margin to position text correctly

  // Set consistent opacity for all borders
  doc.setGState(new doc.GState({ opacity: 0.3 }));

  // Top border
  for (let x = 0; x < width; x += doc.getTextWidth(microText)) {
    doc.text(microText, x, topMargin);
  }

  // Bottom border
  for (let x = 0; x < width; x += doc.getTextWidth(microText)) {
    doc.text(microText, x, height - topMargin);
  }

  // Left border
  for (let y = topMargin; y < height - topMargin; y += borderSpacing) {
    doc.text(microText, sideMargin, y, { angle: 90 });
  }

  // Right border
  for (let y = topMargin; y < height - topMargin; y += borderSpacing) {
    doc.text(microText, width - sideMargin, y, { angle: 90 });
  }

  // Reset opacity to default
  doc.setGState(new doc.GState({ opacity: 1 }));
};

// Update the addSecurityPattern function for subtle background
const addSecurityPattern = (doc) => {
  const { width, height } = doc.internal.pageSize;
  
  // Create layered background pattern
  doc.setGState(new doc.GState({ opacity: 0.03 })); // Very subtle background
  doc.setFontSize(14);
  doc.setTextColor(220, 20, 60); // Crimson red
  
  // Dense, offset pattern
  const spacing = 100;
  const offset = spacing / 2;
  
  // First layer - standard grid
  for (let y = 0; y < height + spacing; y += spacing) {
    for (let x = 0; x < width + spacing; x += spacing) {
      doc.text('VOID SAMPLE', x, y, { angle: 45 });
    }
  }
  
  // Second layer - offset grid for more density
  doc.setGState(new doc.GState({ opacity: 0.02 }));
  for (let y = offset; y < height + spacing; y += spacing) {
    for (let x = offset; x < width + spacing; x += spacing) {
      doc.text('VOID SAMPLE', x, y, { angle: -45 });
    }
  }
  
  doc.setGState(new doc.GState({ opacity: 1 })); // Reset opacity
};

// Update the addSecurityFeatures function
const addSecurityFeatures = async (doc, receiptData) => {
  const { width, height } = doc.internal.pageSize;
  const securityHash = await generateSecurityHash(receiptData);
  
  const fingerprints = [
    {
      text: [
        'DOCUMENT ID',
        securityHash.slice(0, 8)
      ],
      x: 55,
      y: 120,
      opacity: 0.1,
      borderColor: [200, 200, 200] // Very light gray for all borders
    },
    {
      text: [
        'VERIFICATION DATE',
        new Date().toISOString().split('T')[0]
      ],
      x: width - 55,
      y: 120,
      opacity: 0.1,
      borderColor: [200, 200, 200] // Very light gray for all borders
    },
    {
      text: [
        'SECURITY CODE',
        securityHash.slice(-8)
      ],
      x: 55,
      y: height - 80,
      opacity: 0.1,
      borderColor: [200, 200, 200] // Very light gray for all borders
    },
    {
      text: [
        'SAMPLE DOCUMENT',
        'NOT VALID'
      ],
      x: width - 55,
      y: height - 95,
      opacity: 0.1,
      borderColor: [200, 200, 200] // Very light gray for all borders
    }
  ];
  
  // Add each fingerprint with proper formatting
  fingerprints.forEach(({ text, x, y, opacity, borderColor }) => {
    // Draw box with specified color
    doc.setGState(new doc.GState({ opacity: opacity }));
    doc.setDrawColor(...borderColor);
    doc.setLineWidth(0.5);
    doc.roundedRect(x - 40, y - 15, 80, 25, 3, 3);
    
    // Add text
    doc.setFontSize(6);
    doc.text(text[0], x, y - 5, { align: 'center' });
    doc.setFontSize(8);
    doc.setFont(STYLES.fonts.body, 'bold');
    doc.text(text[1], x, y + 5, { align: 'center' });
  });
  
  doc.setGState(new doc.GState({ opacity: 1 }));
};

// Update the watermark to be more visible
const addSampleWatermark = (doc) => {
  const { width, height } = doc.internal.pageSize;
  doc.setGState(new doc.GState({ opacity: 0.15 })); // Increased opacity
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(60); // Larger size
  doc.setTextColor(220, 20, 60); // Crimson red
  
  // Create a denser pattern of watermarks
  for (let y = 50; y < height; y += 120) {
    for (let x = 50; x < width; x += 150) {
      doc.text('SAMPLE', x, y, {
        angle: 45,
      });
    }
  }
  
  doc.setGState(new doc.GState({ opacity: 1 }));
};

// Add verification QR code
const addVerificationQR = async (doc, hash) => {
  const { width } = doc.internal.pageSize;
  const verificationUrl = `https://causeandimpact.org/verify/${hash}`;
  const qrCode = await QRCode.toDataURL(verificationUrl, {
    width: 50,
    margin: 0,
    color: {
      dark: '#2563eb33',
      light: '#ffffff'
    }
  });
  
  doc.addImage(qrCode, 'PNG', width - 90, 40, 50, 50);
};

// Main sample generator function
export const generateSampleReceipt = async (sampleData) => {
  const doc = new jsPDF('p', 'pt', 'letter');
  
  // Generate sample data if none provided
  const defaultSampleData = {
    receiptId: 'SAMPLE-' + Date.now(),
    date: new Date('2024-12-15'),
    donor: {
      name: 'John Doe',
      address: '915 Captivate',
      city: 'Black Rock City',
      state: 'NV',
      zip: '89412'
    },
    type: 'cash',
    amount: 10000.00,
    donationUrl: 'https://causeandimpact.org/donate'
  };

  const sampleCauseData = {
    name: 'Your Sample Cause',
    logo: null
  };

  const data = sampleData || defaultSampleData;

  // Layer 1: Background security pattern (most subtle)
  addSecurityPattern(doc);
  
  // Layer 2: Micro-text border (subtle but visible)
  addMicroTextBorder(doc);
  
  // Layer 3: Security features/fingerprints (visible but professional)
  await addSecurityFeatures(doc, data);
  
  // Layer 4: Sample watermark (more visible)
  addSampleWatermark(doc);
  
  // Layer 5: Content layers (header, body, footer)
  const qrCodeUrl = await generateSampleQRCode(data.donationUrl);
  addSampleHeaderFooter(doc, sampleCauseData, qrCodeUrl, data.receiptId);
  const donationBoxEndY = addSampleDonationBox(doc, data);
  
  // Layer 6: Content - Donor Information
  doc.autoTable({
    startY: donationBoxEndY + 20,
    body: [
      ['Donor Name:', data.donor.name],
      ['Address:', data.donor.address],
      ['City, State, ZIP:', `${data.donor.city}, ${data.donor.state} ${data.donor.zip}`]
    ],
    styles: { 
      fontSize: 10,
      cellPadding: 8,
      textColor: STYLES.colors.textDark
    },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 120 },
      1: { cellWidth: 350 }
    },
    margin: { left: 40, right: 40 }
  });

  // Layer 7: Content - IRS Disclosure
  addSampleIRSDisclosure(doc, sampleCauseData, doc.lastAutoTable.finalY + 20);

  // Final layer: Verification QR
  await addVerificationQR(doc, await generateSecurityHash(data));

  return doc;
};

// Export a function to generate a sample with default data
export const generateDefaultSampleReceipt = async () => {
  return generateSampleReceipt();
}; 